"use client";

import * as React from "react";
import {
  loginWithPassword,

  loginWithLdap,
  loginWithGoogle as loginWithGoogleApi,
  getCurrentUser,
  verifyMFA,
} from "../apis/auth";
import type {
  AuthResponse,
  LoginRequest,
  LdapRequest,
  UserContext,
} from "../lib/types";
import { API_BASE_URL } from "../lib/api";

const TOKEN_STORAGE_KEY = "bi_studio_token";

interface AuthContextType {
  user: UserContext | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
  mfaToken: string | null;
  login: (
    credentials: LoginRequest | LdapRequest,
    isLdap?: boolean
  ) => Promise<AuthResponse>;
  loginWithGoogle: (
    code: string,
    redirectUri: string,
    companyAlias: string,
    app?: "web" | "admin"
  ) => Promise<AuthResponse>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setSessionToken: (token: string) => Promise<void>;
  fetchWithAuth: (endpoint: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<UserContext | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [mfaToken, setMfaToken] = React.useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = React.useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = React.useState(false);

  // Load token from storage on mount
  React.useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedToken) {
      setToken(storedToken);
      // Try to refresh user context
      getCurrentUser(storedToken)
        .then((userContext) => {
          setUser(userContext);
        })
        .catch(() => {
          // Token invalid, clear it
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setToken(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = React.useCallback(
    async (credentials: LoginRequest | LdapRequest, isLdap = false) => {
      try {
        const response: AuthResponse = isLdap
          ? await loginWithLdap(credentials as LdapRequest)
          : await loginWithPassword(credentials as LoginRequest);

        if (response.mfaRequired && response.mfaToken) {
          setMfaRequired(true);
          setMfaToken(response.mfaToken);
          return response;
        }

        if (response.mfaSetupRequired && response.mfaToken) {
          setMfaSetupRequired(true);
          setMfaToken(response.mfaToken);
          return response;
        }

        setToken(response.token!);
        localStorage.setItem(TOKEN_STORAGE_KEY, response.token!);

        // Fetch user context
        const userContext = await getCurrentUser(response.token!);
        setUser(userContext);
        return response;
      } catch (error) {
        // Re-throw so the login form can handle it
        throw error;
      }
    },

    []
  );

  const loginWithGoogle = React.useCallback(
    async (code: string, redirectUri: string, companyAlias: string, app: "web" | "admin" = "web"): Promise<AuthResponse> => {
      try {
        const response = await loginWithGoogleApi(code, redirectUri, companyAlias, app);

        if (response.mfaRequired && response.mfaToken) {
          setMfaRequired(true);
          setMfaToken(response.mfaToken);
          return response;
        }

        if (response.mfaSetupRequired && response.mfaToken) {
          setMfaSetupRequired(true);
          setMfaToken(response.mfaToken);
          return response;
        }

        setToken(response.token!);
        localStorage.setItem(TOKEN_STORAGE_KEY, response.token!);

        // Fetch user context
        const userContext = await getCurrentUser(response.token!);
        setUser(userContext);
        return response;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const verifyMfa = React.useCallback(
    async (code: string) => {
      if (!mfaToken) {
        throw new Error("MFA token missing");
      }

      try {
        const response = await verifyMFA(mfaToken, code);

        setToken(response.token!);
        localStorage.setItem(TOKEN_STORAGE_KEY, response.token!);

        // Fetch user context
        const userContext = await getCurrentUser(response.token!);
        setUser(userContext);

        // Clear MFA state
        setMfaRequired(false);
        setMfaSetupRequired(false);
        setMfaToken(null);
      } catch (error) {
        throw error;
      }
    },
    [mfaToken]
  );

  const logout = React.useCallback(() => {
    setToken(null);
    setUser(null);
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setMfaToken(null);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }, []);

  const refreshUser = React.useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const userContext = await getCurrentUser(token);
      setUser(userContext);
    } catch (error) {
      // Token invalid, logout
      logout();
    }
  }, [token, logout]);

  const setSessionToken = React.useCallback(async (newToken: string) => {
    setToken(newToken);
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);

    try {
      const userContext = await getCurrentUser(newToken);
      setUser(userContext);

      // Clear MFA state
      setMfaRequired(false);
      setMfaSetupRequired(false);
      setMfaToken(null);
    } catch (error) {
      logout();
    }
  }, [logout]);

  const fetchWithAuth = React.useCallback(
    async (endpoint: string, options: RequestInit = {}) => {
      const url = endpoint.startsWith("http")
        ? endpoint
        : `${API_BASE_URL}${endpoint}`;

      return fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });
    },
    [token]
  );

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    mfaRequired,
    mfaSetupRequired,
    mfaToken,


    login,
    loginWithGoogle,
    verifyMfa,
    logout,
    refreshUser,
    setSessionToken,
    fetchWithAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
